import { useManualTenderDrawer } from '@/lib/services/cash-drawers/useManualTenderDrawer'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { useAbility } from '@/lib/casl'
import { refreshManualTenderDrawer } from '@/lib/services/cash-drawers/cash-drawers.preflight'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { RefundSettlementStatus } from '@/lib/enums/RefundSettlementStatus'
import {
  useConfirmCashRefundRequestPaymentMutation,
  useRefundRequestStatus,
} from '@/lib/services/sales-orders/sales-orders.query'
import { formatCurrency, getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import { normalizeLoadedRefundRequestStatus } from './refund-settlement-status'
import type { RefundRequest, RefundStatusPaymentLeg } from '@/lib/services/refunds/refunds.types'
import type { PaymentRefundStatus } from '@/lib/services/sales-order-payments/sales-order-payments.types'

/** The till a cash confirmation records, when Cash Drawer tracking is on. */
interface CashConfirmationContext {
  cashDrawerSessionId?: number
}

const PENDING_LEG_REFUND_STATUSES: Array<PaymentRefundStatus | null> = [
  'pending',
  'processing',
  'cash_pending_confirmation',
]

export function refundPendingMessage(paymentLeg: RefundStatusPaymentLeg): string {
  if (paymentLeg.method !== 'card') {
    return paymentLeg.can_confirm_cash
      ? 'Confirm the handoff after returning the money to the customer.'
      : 'Waiting for the earlier refund payments to finish.'
  }
  if (paymentLeg.provider_method === 'afterpay_clearpay') {
    return 'Afterpay refunds can take up to 10 business days and cannot be canceled.'
  }
  if (paymentLeg.provider_method === 'klarna') {
    return 'Klarna refunds usually take 5–7 business days and may take longer.'
  }
  return 'This refund is still processing. Check again later.'
}

interface RefundSettlementDetailsProps {
  query: ReturnType<typeof useRefundRequestStatus>
  compact?: boolean
  prepareCashConfirmation?: (() => Promise<CashConfirmationContext>) | null
}

export function RefundSettlementDetails({
  query,
  compact = false,
  prepareCashConfirmation = null,
}: RefundSettlementDetailsProps) {
  const { mutateAsync: confirmCash, isPending: isConfirming } =
    useConfirmCashRefundRequestPaymentMutation()
  const status = query.data
  if (!status)
    return query.isError ? (
      <Typography tone="destructive">
        Unable to check this refund. Refresh the order and try again.
      </Typography>
    ) : null
  const pending = status.settlement_status === RefundSettlementStatus.PendingSettlement.Value
  // The order card already shows the amount and destination. Keep the full breakdown
  // for mixed payments and handoffs, and for the standalone refund form.
  const compactPending =
    compact &&
    pending &&
    status.payment_legs?.length === 1 &&
    status.payment_legs[0]?.method === 'card'
  const confirmHandoff = async (paymentId: number) => {
    try {
      const confirmationContext = await prepareCashConfirmation?.()
      await confirmCash({
        refundRequestId: status.active_refund_request_id,
        paymentId,
        ...confirmationContext,
      })
      await query.refetch()
      toast.success('Cash refund confirmed')
    } catch (error) {
      toast.error('Failed to confirm handoff', { description: getErrorMessage(error) })
    }
  }

  return (
    <section aria-label="Refund settlement" className={compact ? 'mt-3 grid gap-3' : 'grid gap-2'}>
      {!compactPending ? (
        <>
          <Typography variant="labelStrong">Refund settlement</Typography>
          <Typography variant="caption">
            {`Requested ${formatCurrency(status.requested_total)} · Settled ${formatCurrency(status.settled_total)} · Failed ${formatCurrency(status.failed_total)} · Pending ${formatCurrency(status.pending_total)}`}
          </Typography>
        </>
      ) : null}
      {status.settlement_status === RefundSettlementStatus.Settled.Value ? (
        <Typography variant="label">Refund completed</Typography>
      ) : null}
      {status.settlement_status === RefundSettlementStatus.Failed.Value ? (
        <Typography variant="label" tone="destructive">
          Refund failed. No money was sent back to the customer.
        </Typography>
      ) : null}
      {status.settlement_status === RefundSettlementStatus.PartiallySettled.Value ? (
        <Typography variant="label" tone="warning">
          Part of this refund failed. Review the outcome before trying again.
        </Typography>
      ) : null}
      {(status.payment_legs ?? []).map((leg) => (
        <div key={leg.payment_id} className="grid gap-1">
          {!compactPending ? (
            <>
              <Typography variant="label">
                {formatCurrency(leg.amount_requested)} · {leg.destination_label}
              </Typography>
              <Typography variant="caption">
                {String(leg.refund_status ?? '').replaceAll('_', ' ')}
              </Typography>
            </>
          ) : null}
          {PENDING_LEG_REFUND_STATUSES.includes(leg.refund_status) ? (
            <Typography variant="caption">{refundPendingMessage(leg)}</Typography>
          ) : null}
          {leg.can_confirm_cash === true ? (
            <Button
              variant="link"
              size="inline-xs"
              disabled={isConfirming || query.isFetching}
              onClick={() => confirmHandoff(leg.payment_id)}
            >
              Confirm Handoff
            </Button>
          ) : null}
        </div>
      ))}
      {query.isError ? (
        <Typography variant="caption" tone="destructive">
          Could not refresh the refund. Your refund has not been resubmitted.
        </Typography>
      ) : null}
      {pending || query.isError ? (
        <Button
          variant="outline"
          size="sm"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          {query.isFetching ? 'Checking…' : 'Check status'}
        </Button>
      ) : null}
    </section>
  )
}

interface RefundRequestProgressProps {
  refundRequest: RefundRequest
  salesOrderId: number
  /** The order's location, for the till cash comes back from (`manualTenderLocationId`). */
  locationId?: number
}

export default function RefundRequestProgress({
  refundRequest,
  salesOrderId,
  locationId,
}: RefundRequestProgressProps) {
  const query = useRefundRequestStatus({
    refundRequestId: refundRequest.id,
    salesOrderId,
    initialStatus: normalizeLoadedRefundRequestStatus(refundRequest),
  })
  const orgId = useRouteOrganizationId()
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const canConfirmCash = query.data?.payment_legs?.some((leg) => leg.can_confirm_cash === true)
  const { statusQuery, workstationsQuery, workstationId, pairedWorkstation, currentDrawerQuery } =
    useManualTenderDrawer({
      organizationId: orgId,
      targetLocationId: locationId,
      canOperateCashDrawers,
      enabled: Boolean(canConfirmCash),
    })
  const prepareCashConfirmation = async (): Promise<CashConfirmationContext> => {
    const context = await refreshManualTenderDrawer({
      canOperateCashDrawers,
      currentDrawerQuery,
      organizationId: orgId,
      pairedWorkstation,
      statusQuery,
      targetLocationId: locationId,
      workstationId,
      workstationsQuery,
    })
    return context.trackingEnabled ? { cashDrawerSessionId: context.session.id } : {}
  }
  return (
    <RefundSettlementDetails
      query={query}
      prepareCashConfirmation={prepareCashConfirmation}
      compact={Number(query.data?.active_refund_request_id) === Number(refundRequest.id)}
    />
  )
}
